import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Spinner } from '@amzn/awsui-components-core';
import { FeedbackItem, FeedbackItemStatistics } from '@amzn/awscat-pattern-library-service-model';

import { IFeedbackServiceClient, FeedbackServiceFactory } from '../../api/FeedbackService';
import { FeedbackRatingSystem, FeedbackRating, FeedbackSource } from './FeedbackTypes';
import { AppContext } from '../../contexts/AppContext';
import { localLog } from '../../loggers/Logger';
import FeedbackModal from './FeedbackModal';

const votedSvgPathMap = new Map([
  [FeedbackRating.Like, '/images/thumb-up-voted.svg'],
  [FeedbackRating.Dislike, '/images/thumb-down-voted.svg'],
]);

const unvotedSvgPathMap = new Map([
  [FeedbackRating.Like, '/images/thumb-up.svg'],
  [FeedbackRating.Dislike, '/images/thumb-down.svg'],
]);

const ratingTypesMap = new Map([
  [FeedbackRatingSystem.Like, [FeedbackRating.Like]],
  [FeedbackRatingSystem.LikeDislike, [FeedbackRating.Like, FeedbackRating.Dislike]],
]);

interface ContentFeedbackProps {
  readonly contentId: string;
  readonly ratingSystem: FeedbackRatingSystem;
  readonly onRating?: (contentId: string, feedbackRating: FeedbackRating) => void;
  readonly feedbackSource?: FeedbackSource;

  /**
   * After the user finishes casting likes/dislikes, whether to show a
   * "request feedback" modal.
   *
   * As we don't support providing written feedback for 'posts' contents (a.k.a.
   * contents from the "what's new") menu, set this to `false` for them,
   * and `true` for the rest.
   *
   * @default true
   */
  readonly requestWrittenFeedback?: boolean;
}

const ContentFeedback = ({
  contentId,
  ratingSystem,
  onRating,
  feedbackSource = FeedbackSource.Guidance,
  requestWrittenFeedback = true,
}: ContentFeedbackProps) => {
  const feedbackServiceClient: IFeedbackServiceClient = useMemo(
    () => FeedbackServiceFactory.create(),
    [],
  );
  // @ts-expect-error TS2345 - strict migration
  const [feedbackStatistics, setFeedbackStatistics] = useState<FeedbackItemStatistics>(null);
  // @ts-expect-error TS2345 - strict migration
  const [userFeedbackItem, setUserFeedbackItem] = useState<FeedbackItem>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const [isVoting, setIsVoting] = useState<{ [key: string]: boolean }>(
    // @ts-expect-error TS2532 - strict migration
    ratingTypesMap.get(ratingSystem).reduce((acc, rs) => {
      // @ts-expect-error TS7053 - strict migration
      acc[rs] = false;
      return acc;
    }, {}),
  );

  const appContext = useContext(AppContext);
  // @ts-expect-error TS18048 - strict migration
  const { userId } = appContext.authnInfo.userProfile;

  useEffect(() => {
    const fetchFeedback = async () => {
      if (contentId) {
        setIsFetching(true);
        try {
          const [feedbackStatResponse, userFeedbackItemResponse] = await Promise.all([
            feedbackServiceClient.listFeedback([contentId], ratingSystem, feedbackSource),
            feedbackServiceClient.getFeedbackByUser(contentId, userId, feedbackSource),
          ]);
          // @ts-expect-error TS2345 - strict migration
          setFeedbackStatistics(feedbackStatResponse.length ? feedbackStatResponse[0] : null);
          // @ts-expect-error TS2345 - strict migration
          setUserFeedbackItem(userFeedbackItemResponse);
        } finally {
          setIsFetching(false);
        }
      }
    };
    fetchFeedback();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hideFeedbackModal = () => setShowFeedbackModal(false);

  const handleVote = async (feedbackRating: FeedbackRating) => {
    let updatedFeedbackStatistics: FeedbackItemStatistics;
    isVoting[feedbackRating] = true;
    setIsVoting({ ...isVoting });
    try {
      if (feedbackStatistics?.hasRated) {
        updatedFeedbackStatistics = await feedbackServiceClient.deleteFeedback(
          contentId,
          ratingSystem,
          feedbackSource,
        );
        if (userFeedbackItem?.Rating !== feedbackRating) {
          updatedFeedbackStatistics = await feedbackServiceClient.submitFeedback(
            contentId,
            userId,
            feedbackRating,
            ratingSystem,
            '',
            feedbackSource,
          );
        }
      } else {
        updatedFeedbackStatistics = await feedbackServiceClient.submitFeedback(
          contentId,
          userId,
          feedbackRating,
          ratingSystem,
          '',
          feedbackSource,
        );
      }
      setFeedbackStatistics(updatedFeedbackStatistics);
      const userFeedbackItemResponse = await feedbackServiceClient.getFeedbackByUser(
        contentId,
        userId,
        feedbackSource,
      );
      // @ts-expect-error TS2345 - strict migration
      setUserFeedbackItem(userFeedbackItemResponse);
      if (onRating) {
        onRating(contentId, feedbackRating);
      }
      if (updatedFeedbackStatistics.hasRated && feedbackSource !== FeedbackSource.Collection) {
        setShowFeedbackModal(true);
      }
    } catch (e) {
      localLog.error(e);
    } finally {
      isVoting[feedbackRating] = false;
      setIsVoting({ ...isVoting });
    }
  };

  const hasRated = (feedbackRating: FeedbackRating) => {
    return feedbackStatistics?.hasRated && userFeedbackItem?.Rating === feedbackRating;
  };

  if (isFetching) {
    return <Spinner size="normal" />;
  }

  const ratingTypes = ratingTypesMap.get(ratingSystem);
  const ratingCounts = feedbackStatistics?.counts || [];
  // @ts-expect-error TS18048 - strict migration
  const ratingButtons = ratingTypes.map((ratingType) => (
    <span key={ratingType}>
      <Button
        loading={isVoting[ratingType]}
        variant="icon"
        // @ts-expect-error TS18048 - strict migration
        disabled={ratingTypes.some((rType) => isVoting[rType])}
        iconUrl={
          hasRated(ratingType) ? votedSvgPathMap.get(ratingType) : unvotedSvgPathMap.get(ratingType)
        }
        iconAlt={`${ratingType} feedback`}
        ariaLabel={`${ratingType} feedback`}
        onClick={() => handleVote(ratingType)}
      />
      {ratingCounts.find((rc) => rc.value === ratingType)?.count ?? '0'}
    </span>
  ));
  return (
    <>
      {ratingButtons}
      {requestWrittenFeedback && (
        <FeedbackModal
          ratingType={userFeedbackItem?.Rating as FeedbackRating}
          showFeedbackModal={showFeedbackModal}
          hideFeedbackModal={hideFeedbackModal}
          contentId={contentId}
        />
      )}
    </>
  );
};

export default ContentFeedback;
